import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  Typography,
  Grid,
  IconButton,
  TextField,
} from '@material-ui/core';
import PrintIcon from '@material-ui/icons/Print';
import CloseIcon from '@material-ui/icons/Close';
import { useCookies } from 'react-cookie';
import { useReactToPrint } from 'react-to-print';
import { ThermalTemplate, A4Template } from './PrintTemplates';
import { formattedCurrency } from '../../../utils/utils';
import './PrintPreviewModal.css';

const PrintPreviewModal = ({ open, handleClose, saleData, credencials, options = {} }) => {
  const [cookies, setCookie] = useCookies(['print_preference']);
  const [printType, setPrintType] = useState(cookies.print_preference || 'thermal');
  const [numberOfPeople, setNumberOfPeople] = useState(options.numberOfPeople || 1);
  const printRef = useRef();

  const isSession = saleData && !!saleData.sale_metadata;
  const total = isSession ? saleData?.sale_metadata?.total : saleData?.value;
  const valuePerPerson = numberOfPeople > 0 ? total / numberOfPeople : total;

  useEffect(() => {
    if (cookies.print_preference) {
      setPrintType(cookies.print_preference);
    }
  }, [cookies.print_preference]);

  // Reset people count when modal opens
  useEffect(() => {
    if (open) {
      setNumberOfPeople(options.numberOfPeople || 1);
    }
  }, [open, options.numberOfPeople]);

  // Critical print styles injected directly into the iframe.
  // This bypasses CORS issues on CDN-hosted deployments (e.g. Cloudflare Pages)
  // where react-to-print can't copy cssRules from cross-origin stylesheets.
  const thermalPageStyle = `
    @page { size: 80mm auto; margin: 0; }
    @media print {
      body { margin: 0; padding: 0; width: 80mm; }
      body * { visibility: visible !important; }
      table { width: 100% !important; border-collapse: collapse !important; }
      .MuiTypography-h6 { font-weight: bold !important; margin-bottom: 5mm !important; font-size: 14pt !important; }
      .MuiTableCell-root { border-bottom: 1px dashed #ccc !important; padding: 4px 0 !important; }
      .MuiTableCell-sizeSmall { padding: 4px 0 !important; }
    }
  `;

  const a4PageStyle = `
    @page { size: A4; margin: 10mm; }
    @media print {
      body { margin: 0; padding: 0; width: 100%; }
      body * { visibility: visible !important; }
      table { width: 100% !important; border-collapse: collapse !important; }
      th { background-color: #f5f5f5 !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
      td, th { border: 1px solid #ddd !important; padding: 8px !important; }
    }
  `;

  const handlePrintFn = useReactToPrint({
    contentRef: printRef,
    pageStyle: printType === 'thermal' ? thermalPageStyle : a4PageStyle,
    onAfterPrint: () => {
      // Remove the iframe react-to-print creates, so it doesn't block next print
      const printIframe = document.getElementById('printWindow');
      if (printIframe) printIframe.remove();
      // Close the modal after printing
      handleClose();
    },
  });

  const handlePrintTypeChange = (event) => {
    const value = event.target.value;
    setPrintType(value);
    setCookie('print_preference', value, { path: '/', maxAge: 31536000 });
  };

  const handlePrint = () => {
    // Clean up any leftover iframe from a previous print
    const oldIframe = document.getElementById('printWindow');
    if (oldIframe) oldIframe.remove();

    // Clean up any leftover Safari clone
    const oldClone = document.getElementById('safari-print-clone');
    if (oldClone) oldClone.remove();

    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

    if (isSafari || isIOS) {
      // Safari/iOS: iframe printing doesn't work.
      // Clone content directly into document.body and use window.print().
      const wrapperClass = printType === 'thermal' ? 'print-wrapper-thermal' : 'print-wrapper-a4';
      const clone = printRef.current.cloneNode(true);
      clone.className = wrapperClass;
      clone.setAttribute('id', 'safari-print-clone');
      document.body.appendChild(clone);

      // Clean up clone when print finishes
      const cleanupClone = () => {
        const el = document.getElementById('safari-print-clone');
        if (el) el.remove();
        window.removeEventListener('afterprint', cleanupClone);
      };
      window.addEventListener('afterprint', cleanupClone);

      // Small delay to let the DOM settle, then print
      setTimeout(() => {
        window.print();
      }, 300);
    } else {
      // Chrome/Firefox/Edge: use react-to-print iframe approach
      // Do NOT call handleClose() here — onAfterPrint handles cleanup
      handlePrintFn();
    }
  };

  if (!saleData) return null;

  // Build options with numberOfPeople for thermal sessions
  const printOptions = {
    ...options,
    numberOfPeople: (printType === 'thermal' && isSession) ? numberOfPeople : 1,
  };
  const templateProps = { data: saleData, credencials, options: printOptions };

  // Show people selector only for thermal + session
  const showPeopleSelector = printType === 'thermal' && isSession;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      onClick={(e) => e.stopPropagation()}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {isSession ? '🖨️ Imprimir Comanda' : 'Pré-visualização da Impressão'}
          </Typography>
          <IconButton onClick={handleClose} size="small"><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <FormControl component="fieldset">
              <FormLabel component="legend">Tipo de Impressão</FormLabel>
              <RadioGroup aria-label="print-type" name="print-type" value={printType} onChange={handlePrintTypeChange}>
                <FormControlLabel value="thermal" control={<Radio color="primary" />} label="Cupom Térmico (80mm)" />
                <FormControlLabel value="a4" control={<Radio color="primary" />} label={isSession ? 'Orçamento A4' : 'Comprovante A4'} />
              </RadioGroup>
            </FormControl>

            {/* People selector: only for thermal + session (comanda) */}
            {showPeopleSelector && (
              <Box mt={2}>
                <TextField
                  margin="dense"
                  label="Número de pagantes"
                  type="number"
                  fullWidth
                  variant="outlined"
                  value={numberOfPeople}
                  onChange={(e) => setNumberOfPeople(Math.max(1, parseInt(e.target.value) || 1))}
                  inputProps={{ min: 1 }}
                  helperText={numberOfPeople > 1
                    ? `Valor por pessoa: ${formattedCurrency(valuePerPerson)}`
                    : 'Deixe 1 para não dividir'
                  }
                />
              </Box>
            )}

            <Box mt={2}>
              <Typography variant="caption" color="textSecondary" display="block">
                Sua preferência será salva automaticamente.
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={8}>
            <Box
              style={{
                backgroundColor: '#f0f0f0',
                padding: '20px',
                borderRadius: '8px',
                maxHeight: '65vh',
                overflowY: 'auto',
                display: 'flex',
                justifyContent: 'center',
                border: '1px solid rgba(0,0,0,0.12)',
              }}
            >
              <Box
                style={{
                  boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                  transform: printType === 'a4' ? 'scale(0.5)' : 'none',
                  transformOrigin: 'top center',
                  marginBottom: printType === 'a4' ? '-400px' : '0',
                  backgroundColor: '#fff',
                  pointerEvents: 'none',
                }}
              >
                {printType === 'thermal'
                  ? <ThermalTemplate {...templateProps} />
                  : <A4Template {...templateProps} />
                }
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* Print content: hidden on screen via CSS class, expanded on @media print */}
        <div className="print-content-hidden">
          <div
            ref={printRef}
            className={printType === 'thermal' ? 'print-wrapper-thermal' : 'print-wrapper-a4'}
          >
            {printType === 'thermal'
              ? <ThermalTemplate {...templateProps} />
              : <A4Template {...templateProps} />
            }
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="secondary">Fechar</Button>
        <Button
          onClick={handlePrint}
          color="primary"
          variant="contained"
          startIcon={<PrintIcon />}
          autoFocus
        >
          Imprimir
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PrintPreviewModal;
